/**
 * Filter Model
 * @author tbroussard
 *
 * This model defines properties and provides helper methods used for Filter search.
 */
import Backbone from 'backbone';
import moment from 'moment';

export default Backbone.Model.extend({
	defaults: {
		transactionType: 'ALL',
		startDate: moment().subtract(90, 'days').toDate(),
		endDate: moment().toDate(),
		freeTextSearch: '',
		isNewSearch: true,
		nextPageToken: '',
		customDatesAreValid: true,
		startDateLimitMoment: moment().subtract(3, 'years'),
		datePickerLocalization: '',
		isClientSideFiltering: false,
		currencyCodes: [],
		selectedCurrency: 'ALL',
		_freeTextDateSearch: '',
		_transactionFilterDateSearch: '',
		isDesktopView: false
	},

	/**
	 * Gets data from UI input values:
	 *	- transaction type
	 *	- valid custom start date
	 *	- valid custom end date
	 * Updates the model with the new values
	 */
	parseFormData (data = {}) {

		// if valid, parse the custom date strings into the model
		if (data.startDate || data.endDate) {
			let isValidDateString = this.dateFormatter.isValidDateString,
				startDate = data.startDate,
				endDate = data.endDate;

			if (startDate && isValidDateString(startDate)) {
				this.set('startDate', startDate);
			}

			if (endDate && isValidDateString(endDate)) {
				this.set('endDate', endDate);
			}
		}

		if (data.transactionType) {
			this.set('transactionType', data.transactionType);
		}

		if (data.freeTextSearch) {
			this.set('freeTextSearch', data.freeTextSearch);
		}

		if (data.isClearFreeTextSearch) {
			this.set('isClearFreeTextSearch', data.isClearFreeTextSearch);
		}

		if (data.isClearFilterSelection) {
			this.set('isClearFilterSelection', data.isClearFilterSelection);
		}

		if (data.isClientSideFiltering) {
			this.set('isClientSideFiltering', data.isClientSideFiltering);
		}

		if (data.transactionFilter) {
			this.set('transactionFilter', data.transactionFilter);
		}

		// currently this is only passed in unit test
		if (data.nextPageToken) {
			this.set('nextPageToken', data.nextPageToken);
		}

		if (data.datePickerLocalization) {
			this.set('datePickerLocalization', data.datePickerLocalization);
		}

		if (data.currencyCodes) {
			this.set('currencyCodes', data.currencyCodes);
		}
	},

	/**
	 * @param dateFormatter - dateFormatter object initialized with a
	 * formatString, for formatting the model date strings into a format
	 * ready for the query
	 *
	 * Note: the dateFormat widget can't just be required from here because
	 * that will cause a loadPublicJSFiles test failure. Re-visit this when
	 * the widget is published as its own NPM module.
	 */
	setDateFormatter (dateFormatter) {
		this.dateFormatter = dateFormatter;
	},

	/**
	 * If the date string is valid, this returns a new string that matches
	 * the suggested format
	 */
	formatCustomDateString (dateString) {
		return this.dateFormatter.isValidDateString(dateString) &&
			this.dateFormatter.formatDateString(dateString);
	},

	/**
	 * Check to see if a date is in the valid date range.
	 * If not valid then it returns either the earliest possible date or
	 * latest possible date (current day) depending whether the string
	 * is too far in the past or future.
	 */
	checkDateStringRangeValidation (dateString) {
		const startDateLimitMoment = this.get('startDateLimitMoment');
		const endDateLimitMoment = moment();
		const dateMoment = moment(dateString, this.dateFormatter.momentDateFormat);

		// if date too early/late return limit date string
		if (dateMoment.isBefore(startDateLimitMoment)) {
			return moment(startDateLimitMoment).format(this.dateFormatter.momentDateFormat);
		} else if (dateMoment.isAfter(endDateLimitMoment)) {
			return moment(endDateLimitMoment).format(this.dateFormatter.momentDateFormat);
		}

		return dateString;
	},

	/**
	 * Helper function to use formatCustomDateString for formatting the
	 * start and end date attributes
	 */
	formatCustomDateAttributes () {
		let formattedStartDate = this.formatCustomDateString(this.get('startDate')),
			formattedEndDate = this.formatCustomDateString(this.get('endDate'));

		if (formattedStartDate) {
			this.set('startDate', formattedStartDate);
		}

		if (formattedEndDate) {
			this.set('endDate', formattedEndDate);
		}
	},

	/**
	 * @param startOrEnd string - either "startDate" or "endDate"
	 * @param dateObj Date
	 */
	setDateFromObject(startOrEnd, dateObj) {
		this.set(startOrEnd, moment(dateObj).format(this.dateFormatter.momentDateFormat));
	},

	/**
	 * compare given start date to what's currently in the model
	 * @return - true if start is different
	 */
	hasDifferentStartDate(proposedStartDate) {
		let currentStartDate = this.get('startDate'),
			newStartDate = moment(proposedStartDate).format(this.dateFormatter.momentDateFormat);

		return currentStartDate !== newStartDate;
	},

	/**
	 * compare given end date to what's currently in the model
	 * @return - true if end is different
	 */
	hasDifferentEndDate(proposedEndDate) {
		let currentEndDate = this.get('endDate'),
			newEndDate = moment(proposedEndDate).format(this.dateFormatter.momentDateFormat);

		return currentEndDate !== newEndDate;
	},

	/**
	 * @param startOrEnd string - either "startDate" or "endDate"
	 */
	getMomentDate(startOrEnd) {
		return moment(this.get(startOrEnd), this.dateFormatter.momentDateFormat);
	},

	/**
	 * Enforce business logic rules and limits on the custom date range
	 *  - start date can not be earlier than 1/1/2013
	 *  - end date can not be earlier than 1/2/2013
	 *  - dates can not be later than today
	 *  - if start date > end date, flip the 2 dates
	 *
	 * All these circumstances will repopulate the model attributes and
	 * trigger messaging.
	 *
	 * MomentJS object names are always postfixed with 'Moment'
	 *
	 * @return validation object with meta data
	 */
	checkDateRangeLimits () {
		// return object. most states are for unit tests
		let validation = {};

		// shortcut because repeated access
		const momentDateFormat = this.dateFormatter.momentDateFormat;

		// user input converted to moment objects
		let startDateInputMoment = moment(this.get('startDate'), momentDateFormat),
			endDateInputMoment = moment(this.get('endDate'), momentDateFormat);

		// Beginning Of Time
		const startDateLimitMoment = this.get('startDateLimitMoment');
		// date of today (for checking if dates are too late
		const todayMoment = moment();

		if (endDateInputMoment.isBefore(startDateInputMoment)) { // Are dates flipped?
			validation.datesFlipped = true;

			// flip the dates for working object to in-order
			const newStartDateInputMoment = endDateInputMoment;
			endDateInputMoment = startDateInputMoment;
			startDateInputMoment = newStartDateInputMoment;

			// flip them in the model so view updates
			const newStartDateAttr = this.get('endDate');
			this.set('endDate', this.get('startDate'));
			this.set('startDate', newStartDateAttr);
		}

		// start date and end date too early? return a search-blocking object
		if (startDateInputMoment.isBefore(startDateLimitMoment) && endDateInputMoment.isBefore(startDateLimitMoment)) {
			validation.bothDatesTooEarly = true;

			// immediately show a message for dates too early
			Backbone.trigger('filter:bothDatesTooEarly');

			// return early, nothing more needs to be done
			return validation;
		} else if (startDateInputMoment.isBefore(startDateLimitMoment)) { // Is start date too early?
			validation.startDateTooEarly = true;
			// force a 2-year search beginning the earliest
			this.set('startDate', startDateLimitMoment.format(momentDateFormat));

			// queue a message for partial date range was searched, to show
			// after collection view render
			Backbone.trigger('filter:startDateTooEarly');
		}

		if (startDateInputMoment.isAfter(todayMoment)) { // Is start date too late?
			validation.startDateTooLate = true;
			this.set('startDate', todayMoment.format(momentDateFormat));
		}

		if (endDateInputMoment.isAfter(todayMoment)) { // Is end date too late?
			validation.endDateTooLate = true;
			this.set('endDate', todayMoment.format(momentDateFormat));
		}

		return validation;
	},

	/**
	 * Fire global event for doing filter search or next page fetch
	 */
	doSearch (options = {}) {
		if (options.nextPageToken) {
			this.set('isNewSearch', false);
			this.set('nextPageToken', options.nextPageToken);
		} else {
			this.set('isNewSearch', true);
			this.set('nextPageToken', '');
		}

		Backbone.trigger('filter:doSearch', this.getSearchParameters());
	},

	/**
	 * Set model properties to last-year custom search state and fire
	 * global event for doing last year search
	 */
	doLastYearSearch () {
		let momentDateFormat = this.dateFormatter.momentDateFormat;

		this.set('endDate', moment().format(momentDateFormat));
		this.set('startDate', moment().subtract(1, 'year').format(momentDateFormat));

		Backbone.trigger('filter:doSearch', this.getSearchParameters());
	},

	/**
	 * Return an object suited for passing along in the 'filter:doSearch' event
	 */
	getSearchParameters (dateFormatter) {
		let params = {
			transactionType: this.get('transactionType'),
			isNewSearch: this.get('isNewSearch'),
			nextPageToken: this.get('nextPageToken'),
			freeTextSearch: this.get('freeTextSearch'),
			isClearFreeTextSearch: this.get('isClearFreeTextSearch'),
			isClearFilterSelection: this.get('isClearFilterSelection'),
			isClientSideFiltering: this.get('isClientSideFiltering'),
			transactionFilter: this.get('transactionFilter'),
			selectedCurrency: this.get('selectedCurrency')
		};

		/* Don't have these part of the object unless they have value or
		 * else Load More will break */
		// TODO make Load More not so breaky
		if (this.get('startDate')) {
			params.startDate = this.dateFormatter.normalizeDateString(this.get('startDate'));
		}

		if (this.get('endDate')) {
			params.endDate = this.dateFormatter.normalizeDateString(this.get('endDate'));
		}

		return params;
	},

	/**
	 * Define here what "has a start date" or "has an end date" really means
	 * If values are not string type, throw an Error?
	 */
	hasStartDate() {
		return this.get('startDate') !== '';
	},
	hasEndDate() {
		return this.get('endDate') !== '';
	},

	/**
	 * method to fire FPTI tracking when a search is performed
	 */
	triggerSearchFPTI () {
		let searchTerm = this.get('freeTextSearch');
		if (searchTerm) {
			Backbone.trigger('trackLink', null, searchTerm, 'main:walletweb:activity:search:', null, 'link');
		}
	},

	/**
	 * method to fire FPTI tracking when a filter is added or removed
	 *
	 * @param isTxnFilterAdded - boolean whether filter is being added or removed
	 * @param txnFilter - string specifying the txn filter being toggled
	 */
	triggerTxnFilterFPTI (txnFilter, isTxnFilterAdded) {
		let pageName = '';

		txnFilter = txnFilter.replace(/_|-|\./g, '').toLowerCase();

		if (!isTxnFilterAdded) {
			txnFilter = txnFilter + '_close';
		}

		pageName = 'main:walletweb:activity:advancedfilters:' + txnFilter + ':';
		Backbone.trigger('trackLink', null, txnFilter, pageName, null, 'link');
	},

	/**
	 * method to check if the currency selector is not filtering a specific
	 * currency; is set to show all currencies
	 */
	isAllCurrenciesSelected() {
		return this.get('selectedCurrency') === 'ALL';
	}
});



// WEBPACK FOOTER //
// ./model/activity/filterModel.es6